import { useEffect, useMemo, useState } from 'react';

import { PATHS } from 'routes/routes.constants';

import useMatchPath from '@polyai/common/hooks/useMatchPath';
import { useRouter } from '@polyai/common/hooks/useRouter';
import { pluralise } from '@polyai/common/lib/stringHelper';

import useAccount from 'hooks/useAccount';
//import useExtendTrial from 'hooks/useExtendTrial';
import { generateNextPath } from 'lib/routeHelper';
import * as Styled from '../Banner.styled';
import { TRIAL_BANNER_COPY } from './Banner.uiCopy';
import BannerButton from './components/BannerButton';
import BannerTag from './components/BannerTag';
import ExtendTrialModal from './ExtendTrialModal';
import type { BannerHook } from './types';
import { computeTrialFromExpiry } from './utils';

const EXCLUDED_ROUTES = ['/agent-wizard/*'];

const TrialBannerContent = ({ daysLeft }: { daysLeft: number }) => {
  const pluralisedDay = pluralise('day', daysLeft);
  const [isModalOpen, setIsModalOpen] = useState(false);
  //const { canExtend } = useExtendTrial(); //temporarily excluded

  return (
    <>
      <Styled.Wrapper>
        <Styled.BannerGroup>
          <BannerTag daysLeft={daysLeft} pluralisedDay={pluralisedDay} />
          <Styled.Text>
            {TRIAL_BANNER_COPY.text.PLG.message(daysLeft, pluralisedDay)}
          </Styled.Text>
        </Styled.BannerGroup>
        <BannerButton
          pendoId="plg-banner-contact-button"
          label={TRIAL_BANNER_COPY.text.PLG.contactText}
          onClick={() =>
            window.open(
              'https://poly.ai/request-a-demo/',
              '_blank',
              'noopener,noreferrer',
            )
          }
        />
      </Styled.Wrapper>
      <ExtendTrialModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};

export const useTrialBanner: BannerHook = () => {
  const { account, isLoading, accountId } = useAccount();
  const { pushUrl } = useRouter();
  const matchPath = useMatchPath();

  const trial = useMemo(() => {
    if (!account) {
      return null;
    }

    const { account_restrictions } = account;

    if (account_restrictions?.trial_expires) {
      return computeTrialFromExpiry(account_restrictions.trial_expires);
    }

    return null;
  }, [account]);

  const isAccountDisabled =
    !isLoading && account?.account_restrictions?.disabled === true;

  const isTrialExpired =
    !isLoading &&
    account?.account_restrictions &&
    trial !== null &&
    trial.daysLeft <= 0;

  useEffect(() => {
    if (isAccountDisabled) {
      pushUrl(
        generateNextPath(
          PATHS.TRIAL_EXPIRED,
          { accountId },
          { disabled: 'true' },
        ),
      );
    }
  }, [isAccountDisabled, pushUrl, accountId]);

  useEffect(() => {
    if (isTrialExpired) {
      pushUrl(
        generateNextPath(PATHS.TRIAL_EXPIRED, { accountId }, { plg: 'true' }),
      );
    }
  }, [isTrialExpired, pushUrl, accountId]);

  const isExcludedRoute = EXCLUDED_ROUTES.some(route =>
    matchPath(route, false),
  );
  const shouldShow =
    !isLoading && trial !== null && !isTrialExpired && !isExcludedRoute;

  return shouldShow
    ? () => <TrialBannerContent daysLeft={trial.daysLeft} />
    : null;
};
